// Material Dashboard 2 React example components
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "components/DashboardNavbar";
import MDBox from "components/MDBox";
import { Grid, Card, Stack } from "@mui/material";
import ComplexStatisticsCard from "components/Cards/ComplexStatisticsCard";
import useLocale from "context/useLocale";
import { overallPerformance, getDashboardStats } from "services/Insights";
import { fetchSubscriptionById } from "services/Showrooms";
import { useMaterialUIController, setErrorSB } from "context";
import { useEffect, useState } from "react";
import MDTypography from "components/MDTypography";
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";

function SubscriptionInfo() {
  const locale = useLocale();
  const [controller, dispatch] = useMaterialUIController();
  const { currentUser } = controller;

  const [showroomSubscription, setShowroomSubscription] = useState(null);

  const loadShowroomSubscription = (showroomId) => {
    fetchSubscriptionById(showroomId).then(
      (res) => {
        setShowroomSubscription(res.data.data);
      },
      (err) => setErrorSB(dispatch, `${err.response.data.error.message}`)(err)
    );
  };

  useEffect(() => {
    if (currentUser?.showroom_id) {
      loadShowroomSubscription(currentUser.showroom_id);
    }
  }, [currentUser]);

  if (!showroomSubscription) {
    return null;
  }

  return (
    <MDBox mt={4}>
      <Card>
        {!showroomSubscription ? (
          <MDBox p={3}>
            <MDTypography variant="h6" gutterBottom>
              {locale("subscriptionInfo")}
            </MDTypography>
            <CircularProgress />
          </MDBox>
        ) : (
          <MDBox p={3}>
            <MDTypography variant="h6" gutterBottom>
              {locale("subscriptionInfo")} - {showroomSubscription.package.name}
            </MDTypography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    {showroomSubscription.package.is_featured_showroom ? (
                      <CheckCircle color="success" />
                    ) : (
                      <RadioButtonUnchecked />
                    )}
                    <MDTypography variant="body2">{locale("featuredShowroom")}</MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    {showroomSubscription.package.can_manage_branches ? (
                      <CheckCircle color="success" />
                    ) : (
                      <RadioButtonUnchecked />
                    )}
                    <MDTypography variant="body2">{locale("canManageBranches")}</MDTypography>
                  </MDBox>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <MDBox>
                    <MDTypography variant="body2">
                      {locale("listingsLeft")}: {showroomSubscription.listings_count_left} /{" "}
                      {showroomSubscription.package.allowed_listings_count}
                    </MDTypography>
                    <MDTypography variant="body2">
                      {locale("featuredListingsLeft")}:{" "}
                      {showroomSubscription.featured_listings_count_left} /{" "}
                      {showroomSubscription.package.allowed_featured_listings_count}
                    </MDTypography>
                  </MDBox>
                </Stack>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </Card>
    </MDBox>
  );
}

function formatCount(count) {
  if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
  if (count >= 1e3) return `${(count / 1e3).toFixed(1)}k`;
  return count.toLocaleString();
}

const defaultStats = {
  vehicles: { count: 0, percentage: 0 },
  users: { count: 0, percentage: 0 },
  showrooms: { count: 0, percentage: 0 },
  service_centers: { count: 0, percentage: 0 },
};

function SuperAdminOnlyInsights() {
  const locale = useLocale();
  const [, dispatch] = useMaterialUIController();
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStats()
      .then((res) => {
        setStats(res.data.data || defaultStats);
      })
      .catch((err) =>
        setErrorSB(
          dispatch,
          err.response?.data?.error?.message || "Failed to load dashboard stats"
        )(err)
      )
      .finally(() => setLoading(false));
  }, [dispatch]);

  if (loading) {
    return (
      <Grid item xs={12}>
        <MDBox display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </MDBox>
      </Grid>
    );
  }

  const cards = [
    {
      key: "vehicles",
      color: "dark",
      icon: "directions_car",
      titleKey: "vehicles",
    },
    {
      key: "users",
      color: "info",
      icon: "person",
      titleKey: "users",
    },
    {
      key: "showrooms",
      color: "success",
      icon: "store",
      titleKey: "showrooms",
    },
    {
      key: "service_centers",
      color: "primary",
      icon: "design_services",
      titleKey: "service_centers",
    },
  ];

  return (
    <>
      {cards.map(({ key, color, icon, titleKey }) => {
        const { count, percentage } = stats[key] || defaultStats[key];
        const amount = percentage >= 0 ? `+${percentage}%` : `${percentage}%`;
        return (
          <Grid item xs={12} md={6} lg={3} key={key}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color={color}
                icon={icon}
                title={locale(titleKey)}
                count={formatCount(count)}
                percentage={{
                  color: percentage >= 0 ? "success" : "warning",
                  amount,
                  label: locale("thanLastMonth"),
                }}
              />
            </MDBox>
          </Grid>
        );
      })}
    </>
  );
}

function Dashboard() {
  const locale = useLocale();
  const [controller, dispatch] = useMaterialUIController();
  const { vehicleTypes, currentUser } = controller;
  /**
   * @typedef {Object} OverallPerformanceData
   * @property {number} total_listings - Total number of listings
   * @property {number} total_listings_until_last_week - Total listings from last week
   * @property {number} total_listings_change_rate - Change rate of total listings
   * @property {number} total_active_listings - Total number of active listings
   * @property {number} total_active_listings_yesterday - Total active listings from yesterday
   * @property {number} active_listings_change_rate - Change rate of active listings
   * @property {number} total_views_this_month - Total views this month
   * @property {number} total_views_last_month - Total views last month
   * @property {number} views_change_rate - Change rate of views
   */ const [overallPerformanceData, setOverallPerformanceData] = useState({
    total_listings: 0,
    total_listings_until_last_week: 0,
    total_listings_change_rate: 0,
    total_active_listings: 0,
    total_active_listings_yesterday: 0,
    active_listings_change_rate: 0,
    total_views_this_month: 0,
    total_views_last_month: 0,
    views_change_rate: 0,
  });

  const [vehicleTypesOverallPerformanceData, setVehicleTypesOverallPerformanceData] = useState({});

  const loadInsights = (vehicleType = null) => {
    if (!vehicleType) {
      overallPerformance().then(
        (res) => {
          setOverallPerformanceData(res.data.data);
        },
        (err) => setErrorSB(dispatch, `${err.response.data.error.message}`)(err)
      );
    } else {
      overallPerformance(vehicleType.value).then(
        (res) => {
          setVehicleTypesOverallPerformanceData((old) => ({
            ...old,
            [vehicleType.value]: { type: vehicleType, performance: res.data.data },
          }));
        },
        (err) => setErrorSB(dispatch, `${err.response.data.error.message}`)(err)
      );
    }
  };

  useEffect(() => {
    loadInsights();
  }, []);

  useEffect(() => {
    if (vehicleTypes) {
      vehicleTypes.forEach((vehicleType) => {
        loadInsights(vehicleType);
      });
    }
  }, [vehicleTypes]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SubscriptionInfo />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {currentUser.roles.includes("admin") ? <SuperAdminOnlyInsights /> : null}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="design_services"
                title={locale("totalListings")}
                count={overallPerformanceData.total_listings}
                percentage={{
                  color:
                    overallPerformanceData.total_listings_change_rate > 0 ? "success" : "warning",
                  amount: `${overallPerformanceData.total_listings_change_rate}%`,
                  label: locale("thanLastWeek"),
                }}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="design_services"
                title={locale("totalActiveListings")}
                count={overallPerformanceData.total_active_listings}
                percentage={{
                  color:
                    overallPerformanceData.active_listings_change_rate > 0 ? "success" : "warning",
                  amount: `${overallPerformanceData.active_listings_change_rate}%`,
                  label: locale("thanYesterday"),
                }}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="design_services"
                title={locale("totalViewsThisMonth")}
                count={overallPerformanceData.total_views_this_month}
                percentage={{
                  color: overallPerformanceData.views_change_rate > 0 ? "success" : "warning",
                  amount: `${overallPerformanceData.views_change_rate}%`,
                  label: locale("thanLastMonth"),
                }}
              />
            </MDBox>
          </Grid>

          {Object.values(vehicleTypesOverallPerformanceData).map((vehicleTypePerformance) => (
            <Grid item xs={12} key={`${vehicleTypePerformance.type.value}typePerformance`}>
              <Grid item xs={12}>
                <MDTypography component="h1" color="text" fontWeight="medium">
                  {vehicleTypePerformance.type.label}
                </MDTypography>
              </Grid>
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="primary"
                      icon={vehicleTypePerformance.type.icon}
                      title={locale("totalActiveListings")}
                      count={vehicleTypePerformance.performance.total_active_listings}
                      percentage={{
                        color:
                          vehicleTypePerformance.performance.active_listings_change_rate > 0
                            ? "success"
                            : "warning",
                        amount: `${vehicleTypePerformance.performance.active_listings_change_rate}%`,
                        label: locale("thanYesterday"),
                      }}
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="primary"
                      icon={vehicleTypePerformance.type.icon}
                      title={locale("totalViewsThisMonth")}
                      count={vehicleTypePerformance.performance.total_views_this_month}
                      percentage={{
                        color:
                          vehicleTypePerformance.performance.views_change_rate > 0
                            ? "success"
                            : "warning",
                        amount: `${vehicleTypePerformance.performance.views_change_rate}%`,
                        label: locale("thanLastMonth"),
                      }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
